/**
 * ChartEngine — Feature-flagged chart component
 * 
 * Uses TradingView Lightweight Charts (Apache 2.0) by default.
 * Falls back to the legacy SVG chart when the feature flag is disabled.
 * 
 * Feature flag: localStorage key "USE_LW_CHARTS"
 *   - "true" (default): Use lightweight-charts library
 *   - "false": Use legacy SVG TradingViewLWChart
 * 
 * Both engines accept identical props, so this is a transparent wrapper.
 * 
 * "Do not remove the existing SVG chart until the feature flag has been
 *  stable in production for multiple market sessions."
 */
import { lazy, Suspense, useMemo } from "react";

// Lazy-load both engines to keep bundle efficient
const LightweightChartsEngine = lazy(() => import("./LightweightChartsEngine"));
const LegacySVGChart = lazy(() => import("./TradingViewLWChart"));

/**
 * Is the lightweight-charts engine active? Exported because callers need to know BEFORE they
 * render controls: the legacy SVG engine has no interval path and reads any value it is given as
 * a RANGE key (`PERIOD_MAP[timeframe] || "1mo"`), so an interval selector shown above it would
 * silently render one month of range data under an active "5m" tab. A control that cannot be
 * honoured must not be offered.
 */
export function isLWChartsEnabled() {
  try {
    return localStorage.getItem(FEATURE_FLAG_KEY) !== "false";
  } catch {
    return true;
  }
}

// Read feature flag — defaults to true (LW charts enabled)
function useLWChartsEnabled() {
  try {
    const flag = localStorage.getItem("USE_LW_CHARTS");
    if (flag === "false") return false;
    return true; // Default: enabled
  } catch {
    return true;
  }
}

const LoadingFallback = () => (
  <div className="w-full h-full flex items-center justify-center bg-white" style={{ minHeight: 280 }}>
    <div className="animate-spin h-8 w-8 border-2 border-[#00b386] border-t-transparent rounded-full" />
  </div>
);

export default function ChartEngine(props) {
  const useLW = useLWChartsEnabled();

  // Memoize the chart component selection
  const ChartComponent = useMemo(() => {
    return useLW ? LightweightChartsEngine : LegacySVGChart;
  }, [useLW]);

  return (
    <Suspense fallback={<LoadingFallback />}>
      <ChartComponent {...props} />
    </Suspense>
  );
}

// Export flag name for tests
export const FEATURE_FLAG_KEY = "USE_LW_CHARTS";
